// @greenlight-spec: 15-database-schema.md
// @greenlight-spec: 17-code-organization.md
// @greenlight-spec: decisions/0026-integration-catalog-and-connection-profiles.md
// The single auth axis (docs/09 § Tiered Auth Model). How the proxy obtains the
// upstream credential — HTTP modes for API integrations, database modes for
// connected databases (ADR-0030). There is no `hybrid` mode: a provider that
// supports more than one identity model is registered as two single-mode
// integrations (docs/09 § Integrations that support both identity models).
export const INTEGRATION_AUTH_MODES = [
    'service_account', // Tier 1 — static key / bearer / basic; wire placement in auth_config.auth_inject
    'client_credentials', // Tier 2 — OAuth2 client-credentials; the proxy mints a bearer token
    'user_delegated', // Tier 3 — user passthrough; the upstream sees the signed-in user (docs/48)
    'workload_identity', // connected DB — the proxy connects as the platform data identity; no stored secret
    'sql_password', // connected DB — SQL auth; username in auth_config, vaulted password
    'service_principal', // connected DB — an Entra app in the target's tenant; client_id + tenant_id in auth_config, vaulted client secret
    'system', // system integration — the control plane serves the data itself via an internal proxy branch (ADR-0036); no upstream, no stored secret
];
// How a `user_delegated` integration obtains the signed-in user's upstream token
// (docs/48 §Terminology, docs/15 §integrations `auth_config.mechanism`). The
// vocabulary the config column may hold; which of them a given Greenlight
// serves is the proxy engine's answer, checked at registration.
export const USER_DELEGATED_MECHANISMS = [
    'authorization_code', // per-user OAuth: a sign-in Greenlight hosts, one stored token per (user, credential)
];
// The lifecycle of a per-user OAuth user connection (docs/15 §user_connections,
// docs/48 §Per-user OAuth): only `active` serves a call; `needs_reauth` means the
// person must connect again; `revoked` is kept for the audit trail and replaced
// on the next connect.
export const USER_CONNECTION_STATUSES = ['active', 'needs_reauth', 'revoked'];
/** Whether an integration is per-user OAuth: each person connects their own account to it. */
export function isPerUserOauthIntegration(authMode, authConfig) {
    return (authMode === 'user_delegated' &&
        typeof authConfig === 'object' &&
        authConfig !== null &&
        authConfig['mechanism'] === 'authorization_code');
}
/**
 * A Snowflake role as the connect leg's scope and the SQL API's `role` name it:
 * an unquoted identifier, upper-cased the way Snowflake stores it. Null for a name
 * that would need quoting (`session:role-encoded:`), which a credential refuses.
 */
export function normalizeCredentialRole(role) {
    const trimmed = role.trim();
    return /^[A-Za-z_][A-Za-z0-9_$]{0,254}$/.test(trimmed) ? trimmed.toUpperCase() : null;
}
/**
 * The scopes a user connection is made with, and refreshed with: the integration's
 * scopes plus, for a credential that is one role, `session:role:<role>` (docs/48
 * §Snowflake). The connect leg and the proxy's refresh share it so a refresh asks
 * for exactly what the user consented to.
 */
export function userConnectionScopes(integrationScopes, credentialRole) {
    const scopes = integrationScopes.filter((s) => s.trim() !== '');
    return credentialRole ? [...scopes, `session:role:${credentialRole}`] : scopes;
}
// Modes whose credential rows are grant-binding handles only — nothing is vaulted
// or rotated. `workload_identity` authenticates as the platform data identity
// (ADR-0030); `system` is served by the control plane itself (ADR-0036).
export const SECRETLESS_AUTH_MODES = ['workload_identity', 'system'];
export function isSecretlessAuthMode(mode) {
    return SECRETLESS_AUTH_MODES.includes(mode ?? '');
}
// Auth modes valid for a connected database (`engine` set); disjoint from the
// HTTP modes at registration (the contract enforces the pairing). `service_principal`
// is an Entra app registered in the database's own tenant (ADR-0030): the Entra
// option cross-tenant (a managed identity authenticates within its own tenant, and
// Azure SQL forbids cross-tenant SP auth; `sql_password` stays available), and a
// same-tenant alternative when IT prefers a dedicated app identity.
export const CONNECTED_DB_AUTH_MODES = [
    'workload_identity',
    'sql_password',
    'service_principal',
];
// Connected-DB auth modes deliverable `injected` to a native driver (ADR-0030 §5).
// `workload_identity` is the platform data identity, which never leaves the
// control plane.
export const INJECTABLE_CONNECTED_DB_AUTH_MODES = ['service_principal'];
export function isInjectableConnectedDbAuthMode(mode) {
    return INJECTABLE_CONNECTED_DB_AUTH_MODES.includes(mode ?? '');
}
// An injected connected database's `env_var_name` is a prefix: the pod receives
// one env var per connection field, never a connection string (docs/29).
export const CONNECTED_DB_INJECTED_ENV_SUFFIXES = [
    'HOST',
    'PORT',
    'DATABASE',
    'TENANT_ID',
    'CLIENT_ID',
    'CLIENT_SECRET',
];
// Keeps every derived name inside the 128-character env-var grammar.
export const CONNECTED_DB_ENV_PREFIX_MAX_LENGTH = 128 - Math.max(...CONNECTED_DB_INJECTED_ENV_SUFFIXES.map((s) => s.length + 1));
/** The env var each field of an injected connected database is delivered under. */
export function connectedDbInjectedEnvNames(prefix) {
    return Object.fromEntries(CONNECTED_DB_INJECTED_ENV_SUFFIXES.map((suffix) => [suffix, `${prefix}_${suffix}`]));
}
/**
 * Every env var an injected integration puts in the pod: its `env_var_name`
 * for an HTTP integration, or the derived per-field names for a connected
 * database.
 */
export function injectedEnvNamesFor(envVarName, connectedDatabase) {
    return connectedDatabase ? Object.values(connectedDbInjectedEnvNames(envVarName)) : [envVarName];
}
// Engines a connected database can be registered with. Both speak TDS on 1433 and
// share the proxy's single MSSQL dialect; an engine on another wire protocol needs
// its own driver contract and tests, and the executor's engine guard widened with it.
export const CONNECTED_DB_ENGINES = ['azure-sql', 'fabric-warehouse'];
const CONNECTED_DB_ENGINE_LABELS = {
    'azure-sql': 'Azure SQL',
    'fabric-warehouse': 'Fabric Warehouse',
};
// Display label for a connected-database engine key; falls back to the raw key
// so an unknown (future) engine still renders.
export function connectedDbEngineLabel(engine) {
    return CONNECTED_DB_ENGINE_LABELS[engine] ?? engine;
}
// Where a `service_account` static credential goes on the wire — the shape the proxy
// swaps on (T-064). Stored under `auth_config.auth_inject`; never repeats `auth_mode`.
export const AUTH_INJECT_KINDS = [
    'bearer', // Authorization: Bearer <secret>
    'basic', // Authorization: Basic base64(username:secret)
    'custom_header', // <header_name>: <secret>
    'query_param', // ?<param_name>=<secret>
    'multi_header', // several headers, each value drawn from a named credential field
];
// The display taxonomy (docs/09). Derived from `auth_mode`, never stored —
// `service_account`/`client_credentials` present one shared org identity to the
// upstream; `user_delegated` presents the signed-in user; the database modes mark
// a connected database. A `system` integration reads as service-identity: the
// caller is the app, not the end user (ADR-0036). The return is a subset of the
// broader `AuthCategorySchema` (which also covers provisioned-resource integrations).
// An integration with an `engine` is a connected database whatever its mode, so a
// Fabric Warehouse that acts as the signed-in user still reads as a database. A
// catalog profile that mints per user under another mode (Box) passes
// `actsAsSignedInUser` from `catalogProfileActsAsSignedInUser`, so it never reads
// as a shared identity.
export function authCategoryFromMode(mode, { engine = null, actsAsSignedInUser = false, } = {}) {
    if (engine !== null)
        return 'connected-database';
    if (mode === 'user_delegated' || actsAsSignedInUser)
        return 'user-delegated';
    if (mode === 'workload_identity' || mode === 'sql_password' || mode === 'service_principal') {
        return 'connected-database';
    }
    return 'service-identity';
}
export const PERMISSION_STATUSES = ['pending', 'granted', 'denied', 'revoked'];
export const INTEGRATION_APPROVAL_MODES = ['auto', 'manual'];
export const INTEGRATION_STATUSES = ['active', 'degraded', 'inactive', 'deleted'];
export const AVAILABLE_INTEGRATION_STATUSES = ['active', 'degraded'];
export const VISIBLE_INTEGRATION_STATUSES = ['active', 'degraded', 'inactive'];
export function isIntegrationAvailable(status) {
    return AVAILABLE_INTEGRATION_STATUSES.includes(status);
}
export const PRINCIPAL_TYPES = ['app', 'user'];
//# sourceMappingURL=integration.js.map